import React, { useEffect, useRef, useState } from 'react';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import * as THREE from 'three';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import app from "../../../firebaseConfig";

const StlViewerAR = ({ stlUrl, color = '#cccccc' }) => {
  const videoRef = useRef();
  const canvasRef = useRef();
  const [autoRotate, setAutoRotate] = useState(false);
  const [modelSize, setModelSize] = useState({ width: 0, height: 0, depth: 0 });
  const [cameraZ, setCameraZ] = useState(0);
  const [lightDir, setLightDir] = useState('default');
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });
  const [zoomLevel, setZoomLevel] = useState(45);
  const [cameraDirection, setCameraDirection] = useState({ x: 0, y: 0, z: 0 });
  const [elevation, setElevation] = useState(0);
  const [autoRotateSpeed, setAutoRotateSpeed] = useState(5);
  const [mostrarDatos, setMostrarDatos] = useState(false);

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(err => console.warn("⚠️ No se pudo reproducir el video:", err));
          };
        }
      }).catch((err) => {
        alert("❌ No se pudo acceder a la cámara.");
        console.error(err);
      });

    if (!stlUrl) return;

    const storage = getStorage(app);
    const archivoRef = storageRef(storage, stlUrl);

    getDownloadURL(archivoRef).then((downloadURL) => {
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
      camera.position.set(0, 20, 70);

      const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true, antialias: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.shadowMap.enabled = true;

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);

      const hemiLight = new THREE.HemisphereLight(0xffffbb, 0x080820, 0.4);
      scene.add(hemiLight);

      const dirLight = new THREE.DirectionalLight(0xffffff, 1.5);
      dirLight.castShadow = true;
      dirLight.shadow.mapSize.width = 2048;
      dirLight.shadow.mapSize.height = 2048;
      scene.add(dirLight);

      const updateLightDirection = () => {
        switch (lightDir) {
          case 'top': dirLight.position.set(0, 100, 0); break;
          case 'side': dirLight.position.set(100, 0, 0); break;
          case 'front': dirLight.position.set(0, 0, 100); break;
          default: dirLight.position.set(50, 100, 50); break;
        }
      };

      const planeGeometry = new THREE.PlaneGeometry(200, 200);
      const planeMaterial = new THREE.ShadowMaterial({ opacity: 0.2 });
      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      plane.rotation.x = -Math.PI / 2;
      plane.position.y = -1;
      plane.receiveShadow = true;
      scene.add(plane);

      const controls = new OrbitControls(camera, canvasRef.current);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enableZoom = true;
      controls.autoRotate = autoRotate;
      controls.autoRotateSpeed = autoRotateSpeed;
      controls.enablePan = true;

      const loader = new STLLoader();
      loader.load(downloadURL, geometry => {
        geometry.computeBoundingBox();
        geometry.computeVertexNormals();

        const boundingBox = geometry.boundingBox;
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);
        geometry.translate(-center.x, -center.y, -center.z);

        const size = new THREE.Vector3();
        boundingBox.getSize(size);
        const maxDimension = Math.max(size.x, size.y, size.z);
        const initialScale = 30 / maxDimension;
        geometry.scale(initialScale, initialScale, initialScale);

        const material = new THREE.MeshStandardMaterial({
          color,
          metalness: 0.5,
          roughness: 0.2,
          emissive: new THREE.Color(color).multiplyScalar(0.2),
          emissiveIntensity: 1.0
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.position.y = 5;
        scene.add(mesh);

        const updateInfo = () => {
          const scaledSize = size.clone().multiplyScalar(mesh.scale.x);
          setModelSize({
            width: (scaledSize.x / 10).toFixed(2),
            height: (scaledSize.y / 10).toFixed(2),
            depth: (scaledSize.z / 10).toFixed(2)
          });

          const camPos = camera.position;
          const distance = camPos.distanceTo(mesh.position);
          setCameraZ(distance.toFixed(2));

          const direction = new THREE.Vector3();
          camera.getWorldDirection(direction);
          setCameraDirection({
            x: direction.x.toFixed(2),
            y: direction.y.toFixed(2),
            z: direction.z.toFixed(2)
          });

          const camDir = new THREE.Vector3();
          camera.getWorldDirection(camDir);
          const elevationRadians = Math.asin(camDir.y);
          const elevationDegrees = THREE.MathUtils.radToDeg(elevationRadians);
          setElevation(elevationDegrees.toFixed(2));
        };

        updateLightDirection();
        updateInfo();

        const animate = () => {
          requestAnimationFrame(animate);
          controls.autoRotate = autoRotate;
          controls.update();
          updateLightDirection();
          updateInfo();
          renderer.render(scene, camera);
        };

        animate();
      }, undefined, (err) => {
        console.error("❌ Error cargando STL desde Firebase:", err);
        alert("Error cargando el modelo STL.");
      });
    });

    return () => {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
    };
  }, [stlUrl, color, autoRotate, lightDir, autoRotateSpeed]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
      <video
        ref={videoRef}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0
        }}
        muted
        autoPlay
        playsInline
      />
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
      />

      {/* Botones de luces + botón de Datos */}
      <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
 {/* Botón Rotación unificado estilo píldora */}
<button
  onClick={() => {
    setAutoRotate(prev => !prev);
    setAutoRotateSpeed(prev => prev === 5 ? 8 : 5);
  }}
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    fontSize: '16px',
    borderRadius: '30px',
    background: autoRotate ? '#ff9800' : '#eeeeee',
    color: autoRotate ? 'white' : 'black',
    border: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    cursor: 'pointer'
  }}
>
  <span style={{ fontSize: '18px' }}>🌀</span>
  <span style={{ fontSize: '14px' }}>Rotación</span>
</button>

        <button onClick={() => setLightDir('default')} style={{marginTop:"15px", background: lightDir === 'default' ? '#4caf50' : '', color: lightDir === 'default' ? 'white' : '' }}>💡 Luz Diagonal</button>
        <button onClick={() => setLightDir('top')} style={{ background: lightDir === 'top' ? '#4caf50' : '', color: lightDir === 'top' ? 'white' : '' }}>🔦 Luz Superior</button>
        <button onClick={() => setLightDir('side')} style={{ background: lightDir === 'side' ? '#4caf50' : '', color: lightDir === 'side' ? 'white' : '' }}>➡️ Luz Lateral</button>
        <button onClick={() => setLightDir('front')} style={{ background: lightDir === 'front' ? '#4caf50' : '', color: lightDir === 'front' ? 'white' : '' }}>🔲 Luz Frontal</button>

{/* Botón Datos unificado estilo píldora */}
<button
  onClick={() => setMostrarDatos(prev => !prev)}
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    borderRadius: '30px',
    background: mostrarDatos ? '#2196f3' : '#eeeeee',
    color: mostrarDatos ? 'white' : 'black',
    border: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    cursor: 'pointer',
    marginTop:"15px"
  }}
>
  <span style={{ fontSize: '20px' }}>🔍</span>
  <span>Datos</span>
</button>


      </div>

      {/* Datos técnicos del modelo */}
      {mostrarDatos && (
        <div style={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          zIndex: 2,
          background: 'rgba(0,0,0,0.6)',
          color: 'white',
          padding: '10px',
          borderRadius: '8px',
          fontSize: '12px',
          lineHeight: '1.4'
        }}>
          📏 Dimensiones actuales:<br />
          Ancho: {modelSize.width} cm<br />
          Alto: {modelSize.height} cm<br />
          Profundidad: {modelSize.depth} cm<br />
          🔍 Distancia cámara: {cameraZ} unidades<br />
          📍 Posición cámara: X: {cameraDirection.x}, Y: {cameraDirection.y}, Z: {cameraDirection.z}<br />
          🎯 Elevación: {elevation}°<br />
          💡 Dirección luz: {lightDir.toUpperCase()}
        </div>
      )}

      <button
        onClick={() => window.history.back()}
        style={{
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 2,
          padding: '10px',
          fontSize: '16px'
        }}
      >
        🔙 Salir
      </button>
    </div>
  );
};

export default StlViewerAR;